import { Box, Card, CardContent, InputAdornment, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography, useMediaQuery, useTheme, Fade, Slide, Grid, CircularProgress } from "@mui/material";
import { StyledTableCell, StyledTableRow, HeaderCard, SearchCard, StyledContainer } from "Pages/AdminList/ProfessionalUserList";
import { useEffect, useState } from "react";
import AdminApi from "Service/Admin.api";
import { Chip, keyframes, styled } from "@mui/material";
import ColorData from "Components/CstmTheme/ColorData";
import SearchIcon from '@mui/icons-material/Search';
import { useDispatch } from "react-redux";
import { startLoading, stopLoading } from "../../Redux/Screen/Action";
import { formatDate, getStatusColorHex } from "Utils/Common";
import DownloadIcon from '@mui/icons-material/Download';
import HistoryIcon from '@mui/icons-material/History';
import { IconButton, Tooltip, Divider, Button } from "@mui/material";
import CaseApi from "Service/Case.api";

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

export const StatusChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== "status",
})<{ status?: string }>(({ theme, status }) => {
  const lowercaseStatus = status?.toLowerCase();

  let bgColor = theme.palette.grey[300];
  let textColor = theme.palette.text.primary;

  if (lowercaseStatus === "completed" || lowercaseStatus === "paymentcompleted" || lowercaseStatus === "success") {
    bgColor = theme.palette.success.main;
    textColor = theme.palette.success.contrastText;
  } else if (
    lowercaseStatus === "pending" ||
    lowercaseStatus === "initial consultation" ||
    lowercaseStatus === "initial assessment"
  ) {
    bgColor = theme.palette.warning.main;
    textColor = theme.palette.warning.contrastText;
  } else if (lowercaseStatus === "rejected" || lowercaseStatus === "failed") {
    bgColor = theme.palette.error.main;
    textColor = theme.palette.error.contrastText;
  }

  return {
    fontWeight: 600,
    borderRadius: theme.spacing(3),
    background: bgColor,
    color: textColor,
    animation: (lowercaseStatus === "completed" || lowercaseStatus === "success") ? `${pulse} 2s infinite` : undefined,
  };
});

interface Order {
  id: number;
  pkCase: string;
  orderTypeName: string;
  caseTypeName: string;
  billingCycle?: string;
  clientFullName: string;
  clientEmail: string;
  clientContactNumber: string;
  currentProcessingStatusName: string;
  statusLastUpdated: string;
  assignedProfessionalName: string;
  casePrice: number;
  casePaymentStatus: string;
  caseNumber: number;
}

interface OrderHistoryProps {
  hideHeader?: boolean;
}

const OrderHistory: React.FC<OrderHistoryProps> = ({ hideHeader }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchQuery, setSearchQuery] = useState("");
  const [ordersHistory, setOrdersHistory] = useState<Order[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [userType, setUserType] = useState<string | null>(null);
  const dispatch = useDispatch();
  const [isLoadingInternal, setIsLoadingInternal] = useState(false);

  // Consolidated data fetching
  const fetchData = async (currentPage: number, pageSize: number, query: string) => {
    dispatch(startLoading({ loading: true, loadtext: "" }));
    setIsLoadingInternal(true);
    const persistedData = localStorage.getItem("persist:root");

    if (persistedData) {
      try {
        const parsedRoot = JSON.parse(persistedData);
        const userObj = JSON.parse(parsedRoot.user);
        setUserType(userObj?.myInfo?.userType);
        const userId = userObj?.myInfo?.id;

        const response = await AdminApi.OrderHistory(
          userId,
          currentPage + 1,
          pageSize,
          query
        );

        if (response.success && response.data) {
          setOrdersHistory(response.data.data || []);
          setTotalCount(response.data.totalCount || 0);
        } else {
          setOrdersHistory([]);
          setTotalCount(0);
        }
      } catch (error) {
        console.error("Failed to fetch order history:", error);
      } finally {
        dispatch(stopLoading());
        setIsLoadingInternal(false);
      }
    } else {
      dispatch(stopLoading());
      setIsLoadingInternal(false);
    }
  };

  useEffect(() => {
    fetchData(page, rowsPerPage, searchQuery);
  }, [page, rowsPerPage]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (page === 0) {
        fetchData(0, rowsPerPage, searchQuery);
      } else {
        setPage(0);
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDownloadInvoice = async (caseId: any) => {
    dispatch(startLoading({ loading: true, loadtext: "Generating Invoice..." }));
    try {
      const response = await CaseApi.downloadInvoice({ caseId });
      if (response.success && response.data) {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Invoice_${caseId}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (error) {
      console.error("Download error:", error);
    } finally {
      dispatch(stopLoading());
    }
  };

  const renderTable = () => (
    <TableContainer
      component={Paper}
      sx={{
        mt: 2,
        boxShadow: "0 10px 15px -3px rgba(0,0,0,0.05)",
        bgcolor: "#ffffff",
        borderRadius: "16px",
        overflow: "visible",
      }}
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <StyledTableCell>Case No.</StyledTableCell>
            <StyledTableCell>Order Type</StyledTableCell>
            <StyledTableCell>Case Type</StyledTableCell>
            <StyledTableCell>Assigned Professional</StyledTableCell>
            {(userType === "ADMIN" || userType === "SUPERADMIN" || userType?.toLowerCase() === "e_mitra_user") && (
              <StyledTableCell>Client Details</StyledTableCell>
            )}
            <StyledTableCell>Case Price</StyledTableCell>
            {userType?.toLowerCase() === "e_mitra_user" && (
              <>
                <StyledTableCell>Commission</StyledTableCell>
                <StyledTableCell>Comm. Status</StyledTableCell>
              </>
            )}
            <StyledTableCell>Case Status</StyledTableCell>
            <StyledTableCell>Payment Status</StyledTableCell>
            <StyledTableCell>Created At</StyledTableCell>
            <StyledTableCell>Invoice</StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {ordersHistory.length === 0 ? (
            <TableRow>
              <TableCell colSpan={13} align="center" sx={{ py: 10 }}>
                <Typography variant="body1" sx={{ color: "#64748b" }}>
                  {isLoadingInternal ? "Searching..." : "No matching orders found."}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            ordersHistory.map((order) => (
              <StyledTableRow key={order.id}>
                <TableCell sx={{ fontWeight: 600 }}>
                  {order.caseNumber ? order.caseNumber : order.pkCase}
                </TableCell>
                <TableCell>{order.orderTypeName}</TableCell>
                <TableCell>
                  {order.caseTypeName}
                  {order.billingCycle && (
                    <Typography variant="caption" display="block" sx={{ color: "#64748b", fontWeight: 600 }}>
                      ({order.billingCycle})
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{order.assignedProfessionalName}</TableCell>
                {(userType === "ADMIN" || userType === "SUPERADMIN" || userType?.toLowerCase() === "e_mitra_user") && (
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {order.clientFullName}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#64748b" }}>
                        {order.clientEmail}
                      </Typography>
                      {order.clientContactNumber && (
                        <Typography variant="caption" sx={{ color: "#64748b" }}>
                          {order.clientContactNumber}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                )}
                <TableCell sx={{ fontWeight: 700, color: "#283165" }}>
                  ₹{order.casePrice.toLocaleString()}
                </TableCell>
                {userType?.toLowerCase() === "e_mitra_user" && (
                  <>
                    <TableCell sx={{ fontWeight: "bold" }}>{(order as any).eMitraCommissionAmount || 0}</TableCell>
                    <TableCell>
                      <StatusChip
                        label={(order as any).isEMitraCommissionPaid ? "Paid" : "Pending"}
                        status={(order as any).isEMitraCommissionPaid ? "Completed" : "Pending"}
                      />
                    </TableCell>
                  </>
                )}
                <TableCell>
                  <StatusChip
                    label={order.currentProcessingStatusName}
                    status={order.currentProcessingStatusName}
                  />
                </TableCell>
                <TableCell>
                  <StatusChip
                    label={order.casePaymentStatus || "Pending"}
                    status={order.casePaymentStatus}
                    sx={{
                      background: getStatusColorHex(order.casePaymentStatus).bg,
                      color: getStatusColorHex(order.casePaymentStatus).text,
                    }}
                  />
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  {formatDate(order.statusLastUpdated)}
                </TableCell>
                <TableCell align="center">
                  {(order.casePaymentStatus === "PaymentCompleted" || order.casePaymentStatus === "Success") && (
                    <Tooltip title="Download Invoice" arrow>
                      <IconButton
                        color="primary"
                        onClick={() => handleDownloadInvoice(order.pkCase || order.id)}
                        sx={{
                          bgcolor: "rgba(40, 49, 101, 0.05)",
                          "&:hover": { bgcolor: "rgba(40, 49, 101, 0.1)" }
                        }}
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </StyledTableRow>
            ))
          )}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </TableContainer>
  );

  const renderCards = () => (
    <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 2 }}>
      {ordersHistory.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="body1">No data found</Typography>
        </Paper>
      ) : (
        ordersHistory.map((order) => (
          <Card key={order.id} sx={{
            borderRadius: "16px",
            border: "1px solid rgba(0,0,0,0.05)",
            boxShadow: "0 2px 4px rgba(0,0,0,0.02)"
          }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" mb={2}>
                <Typography variant="subtitle2" sx={{ color: "#64748b", fontWeight: 600 }}>
                  CASE #{order.caseNumber || order.pkCase}
                </Typography>
                <StatusChip
                  size="small"
                  label={order.currentProcessingStatusName}
                  status={order.currentProcessingStatusName}
                />
              </Box>
              <Typography variant="h6" sx={{ fontSize: "1rem", mb: 1 }}>
                {order.orderTypeName} - {order.caseTypeName}
                {order.billingCycle && <Typography component="span" variant="caption" sx={{ ml: 1, color: "#64748b", fontWeight: 600 }}>({order.billingCycle})</Typography>}
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b", mb: 2 }}>
                <strong>Pro:</strong> {order.assignedProfessionalName}
              </Typography>
              <Divider sx={{ my: 1.5 }} />
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" sx={{ fontSize: "1.1rem", color: "#283165", fontWeight: 700 }}>
                  ₹{order.casePrice.toLocaleString()}
                </Typography>
                {(order.casePaymentStatus === "PaymentCompleted" || order.casePaymentStatus === "Success") && (
                  <Button
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleDownloadInvoice(order.pkCase || order.id)}
                    variant="outlined"
                  >
                    Invoice
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        ))
      )}
      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  );

  return (
    <StyledContainer maxWidth="xl">
      <Fade in timeout={800}>
        <Box sx={{ width: "100%", display: "flex", flexDirection: "column", pb: 10 }}>
          {/* Header Section */}
          {!hideHeader && (
            <HeaderCard elevation={8} sx={{ mb: 4 }}>
              <CardContent sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  flexDirection={{ xs: 'column', md: 'row' }}
                  gap={2}
                >
                  <Box display="flex" alignItems="center" gap={2}>
                    <HistoryIcon sx={{ fontSize: { xs: 40, sm: 48, md: 56 } }} />
                    <Box>
                      <Typography
                        variant={isMobile ? "h5" : "h4"}
                        component="h1"
                        fontWeight="bold"
                        color="white"
                        gutterBottom
                      >
                        Order History
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ opacity: 0.9 }}
                        color='white'
                      >
                        Track and manage all case orders and payment statuses
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </HeaderCard>
          )}

          {/* Search and Filters */}
          <Grid container spacing={3} sx={{ mb: 3 }} component={Box}>
            <Grid size={{ xs: 12, md: 8 }}>
              <SearchCard elevation={2}>
                <CardContent sx={{ p: '16px !important' }}>
                  <TextField
                    fullWidth
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by Case No, Client, etc..."
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon color="primary" />
                          </InputAdornment>
                        ),
                        endAdornment: isLoadingInternal && (
                          <InputAdornment position="end">
                            <CircularProgress size={20} />
                          </InputAdornment>
                        )
                      },
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                      },
                    }}
                  />
                </CardContent>
              </SearchCard>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Box display="flex" gap={1} height="100%" alignItems="center">
                <Chip
                  icon={<HistoryIcon />}
                  label={`Total Orders: ${totalCount}`}
                  color="info"
                  variant="outlined"
                  sx={{ borderRadius: 2 }}
                />
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ flex: 1, pr: 0.5 }}>
            <Slide direction="up" in timeout={600}>
              <Box>
                {isMobile ? renderCards() : renderTable()}
              </Box>
            </Slide>
          </Box>
        </Box>
      </Fade>
    </StyledContainer>
  );
};

export default OrderHistory;
